import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { groups } from '../data/mockData';

const DiscoveryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PageDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 700px;
  margin: 0 auto;
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};
`;

const SearchSection = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 500px;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  margin-right: ${({ theme }) => theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-right: 0;
  }
`;

const CreateGroupButton = styled(Link)`
  display: inline-block;
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    color: white;
  }
`;

const FiltersSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const CategoriesFilter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const CategoryButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  background-color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.cardBg};
  color: ${({ theme, active }) => active ? 'white' : theme.colors.text.secondary};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.border};
  }
`;

const SortFilter = styled.select`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const GroupsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const GroupCard = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
  transition: transform ${({ theme }) => theme.transitions.fast}, 
              box-shadow ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const GroupHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const GroupIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.cardBg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin-right: ${({ theme }) => theme.spacing.md};
`;

const GroupInfo = styled.div`
  flex: 1;
`;

const GroupName = styled(Link)`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const GroupCategory = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const GroupContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const GroupDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};
`;

const GroupStats = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const GroupStat = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const JoinButton = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const PopularTag = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.gradients.secondary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const NoResults = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
`;

// Categories for filtering
const CATEGORIES = [
  'All',
  'Technology',
  'Travel',
  'Gaming',
  'Food',
  'Arts',
  'Sports',
  'Music',
  'Education',
];

const GroupDiscoveryPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [sortBy, setSortBy] = useState('popular');

  // Filter groups based on search query and category
  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         group.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || group.category === activeCategory;

    return matchesSearch && matchesCategory;
  });

  // Sort groups based on selected sort option
  const sortedGroups = [...filteredGroups].sort((a, b) => {
    if (sortBy === 'popular') {
      return b.members - a.members;
    } else if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    } else if (sortBy === 'newest') {
      // In a real app, we would sort by creation date
      // For now, we'll just use the ID as a proxy for creation date
      return b.id - a.id;
    }
    return 0;
  });

  const handleJoin = (groupId) => {
    // In a real app, this would send a request to join the group
    alert(`Joined group with ID: ${groupId}`);
  };

  return (
    <DiscoveryContainer>
      <PageHeader>
        <PageTitle>Discover Groups</PageTitle>
        <PageDescription>
          Find and join communities that match your interests. Connect with like-minded people and share your passions.
        </PageDescription>
      </PageHeader>

      <SearchSection>
        <SearchInput
          type="text"
          placeholder="Search for groups by name or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <CreateGroupButton to="/create-group">Create Group</CreateGroupButton>
      </SearchSection>

      <FiltersSection>
        <CategoriesFilter>
          {CATEGORIES.map(category => (
            <CategoryButton
              key={category}
              active={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </CategoryButton>
          ))}
        </CategoriesFilter>

        <SortFilter value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="popular">Most Popular</option>
          <option value="name">Alphabetical</option>
          <option value="newest">Newest</option>
        </SortFilter>
      </FiltersSection>

      {sortedGroups.length > 0 ? (
        <GroupsGrid>
          {sortedGroups.map(group => (
            <GroupCard key={group.id}>
              {group.isPopular && <PopularTag>Popular</PopularTag>}
              <GroupHeader>
                <GroupIcon>{group.icon}</GroupIcon>
                <GroupInfo>
                  <GroupName to={`/group/${group.id}`}>{group.name}</GroupName>
                  <GroupCategory>{group.category}</GroupCategory>
                </GroupInfo>
              </GroupHeader>

              <GroupContent>
                <GroupDescription>{group.description}</GroupDescription>

                <GroupStats>
                  <GroupStat>
                    <StatValue>{group.members.toLocaleString()}</StatValue>
                    <StatLabel>Members</StatLabel>
                  </GroupStat>
                  <GroupStat>
                    <StatValue>{group.posts.length}</StatValue>
                    <StatLabel>Posts</StatLabel>
                  </GroupStat>
                </GroupStats>

                <JoinButton onClick={() => handleJoin(group.id)}>
                  Join Group
                </JoinButton>
              </GroupContent>
            </GroupCard>
          ))}
        </GroupsGrid>
      ) : (
        <NoResults>
          No groups found matching your search criteria.
        </NoResults>
      )}
    </DiscoveryContainer>
  );
};

export default GroupDiscoveryPage;
